import { useQuery } from '@tanstack/react-query';
import { CheckCircle, AlertCircle, Database, FileText, TrendingUp, Package } from 'lucide-react';
import { api } from '../../lib/api';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';

interface DataRequirement {
  entity: string;
  category: 'master_data' | 'transactional_data';
  priority: 'high' | 'medium' | 'low';
  isMapped: boolean;
  mappedSystems: string[];
}

interface AdditionalData {
  entity: string;
  category: 'master_data' | 'transactional_data';
  sourceSystem: string;
  confidence: 'high' | 'medium' | 'low';
}

interface DataSourcingAnalysis {
  totalRequirements: number;
  mappedRequirements: number;
  unmappedRequirements: number;
  additionalDataCount: number;
  coveragePercentage: number;
  requirementDetails: DataRequirement[];
  additionalDataDetails: AdditionalData[];
}

interface DataSourcingOverviewProps {
  caseId: string;
}

export default function DataSourcingOverview({  caseId }: DataSourcingOverviewProps) {
  const { data: analysis, isLoading, error } = useQuery<DataSourcingAnalysis>({
    queryKey: ['dataSourcingAnalysis', caseId],
    queryFn: () => api.get(`/cases/${ caseId}/data-sourcing-analysis`),
    enabled: !!caseId,
  });

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-l-4 border-l-red-500">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-1">Failed to Load Analysis</h4>
              <p className="text-sm text-red-700">
                Unable to analyze data sourcing. Please ensure you have completed the Data Requirements stage.
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!analysis) {
    return null;
  }

  const {
    totalRequirements,
    mappedRequirements,
    unmappedRequirements,
    additionalDataCount,
    coveragePercentage,
    requirementDetails,
    additionalDataDetails
  } = analysis;

  // Group requirements by category
  const masterDataRequirements = requirementDetails.filter(r => r.category === 'master_data');
  const transactionalDataRequirements = requirementDetails.filter(r => r.category === 'transactional_data');

  const mappedMaster = masterDataRequirements.filter(r => r.isMapped);
  const unmappedMaster = masterDataRequirements.filter(r => !r.isMapped);
  const mappedTransactional = transactionalDataRequirements.filter(r => r.isMapped);
  const unmappedTransactional = transactionalDataRequirements.filter(r => !r.isMapped);

  // Group additional data by category
  const additionalMaster = additionalDataDetails.filter(d => d.category === 'master_data');
  const additionalTransactional = additionalDataDetails.filter(d => d.category === 'transactional_data');

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Requirements */}
        <Card className="border-l-4 border-l-blue-500">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-neutral-600">Total Requirements</p>
                <p className="text-2xl font-bold text-neutral-900">{totalRequirements}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Mapped Requirements */}
        <Card className="border-l-4 border-l-green-500">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-neutral-600">Mapped to Systems</p>
                <p className="text-2xl font-bold text-neutral-900">{mappedRequirements}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Unmapped Requirements */}
        <Card className="border-l-4 border-l-amber-500">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-neutral-600">Missing Sources</p>
                <p className="text-2xl font-bold text-neutral-900">{unmappedRequirements}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Additional Data */}
        <Card className="border-l-4 border-l-purple-500">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-neutral-600">Additional Data</p>
                <p className="text-2xl font-bold text-neutral-900">{additionalDataCount}</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Coverage Progress Bar */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-neutral-900">Data Sourcing Coverage</h4>
            <span className="text-sm font-bold text-neutral-900">{coveragePercentage}%</span>
          </div>
          <div className="w-full bg-neutral-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                coveragePercentage >= 80
                  ? 'bg-green-500'
                  : coveragePercentage >= 50
                  ? 'bg-amber-500'
                  : 'bg-red-500'
              }`}
              style={{ width: `${coveragePercentage}%` }}
            />
          </div>
          <p className="text-xs text-neutral-600 mt-2">
            {coveragePercentage >= 80
              ? 'Excellent! Most data requirements are covered by your systems.'
              : coveragePercentage >= 50
              ? 'Good progress. Consider adding more systems to cover missing requirements.'
              : 'More systems needed to cover your data requirements.'}
          </p>
        </div>
      </Card>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* What We Need (Requirements) */}
        <Card>
          <div className="p-4 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              What We Need
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              Data requirements from your business needs
            </p>
          </div>
          <div className="p-4 space-y-4">
            {/* Master Data */}
            <div>
              <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                Master Data ({masterDataRequirements.length})
              </h4>
              <div className="space-y-2">
                {mappedMaster.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Mapped ({mappedMaster.length})
                    </p>
                    <div className="space-y-1">
                      {mappedMaster.map((req) => (
                        <div
                          key={req.entity}
                          className="flex items-start justify-between gap-2 p-2 bg-green-50 border border-green-200 rounded text-xs"
                        >
                          <span className="font-medium text-neutral-900">{req.entity}</span>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {req.mappedSystems.map((sys) => (
                              <span
                                key={sys}
                                className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium"
                              >
                                {sys}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {unmappedMaster.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Missing Source ({unmappedMaster.length})
                    </p>
                    <div className="space-y-1">
                      {unmappedMaster.map((req) => (
                        <div
                          key={req.entity}
                          className="flex items-center justify-between gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs"
                        >
                          <span className="font-medium text-neutral-900">{req.entity}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            req.priority === 'high'
                              ? 'bg-red-100 text-red-700'
                              : req.priority === 'medium'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {req.priority} priority
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Transactional Data */}
            <div>
              <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                Transactional Data ({transactionalDataRequirements.length})
              </h4>
              <div className="space-y-2">
                {mappedTransactional.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Mapped ({mappedTransactional.length})
                    </p>
                    <div className="space-y-1">
                      {mappedTransactional.map((req) => (
                        <div
                          key={req.entity}
                          className="flex items-start justify-between gap-2 p-2 bg-green-50 border border-green-200 rounded text-xs"
                        >
                          <span className="font-medium text-neutral-900">{req.entity}</span>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {req.mappedSystems.map((sys) => (
                              <span
                                key={sys}
                                className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium"
                              >
                                {sys}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {unmappedTransactional.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Missing Source ({unmappedTransactional.length})
                    </p>
                    <div className="space-y-1">
                      {unmappedTransactional.map((req) => (
                        <div
                          key={req.entity}
                          className="flex items-center justify-between gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs"
                        >
                          <span className="font-medium text-neutral-900">{req.entity}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            req.priority === 'high'
                              ? 'bg-red-100 text-red-700'
                              : req.priority === 'medium'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {req.priority} priority
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Where We Can Get It (Systems + Additional) */}
        <Card>
          <div className="p-4 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              What's Available
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              Additional data discovered in your systems
            </p>
          </div>
          <div className="p-4 space-y-4">
            {additionalDataCount === 0 ? (
              <div className="text-center py-8">
                <TrendingUp className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                <p className="text-sm text-neutral-600">
                  No additional data found yet. As you add systems, we'll discover data opportunities beyond your initial requirements.
                </p>
              </div>
            ) : (
              <>
                {/* Additional Master Data */}
                {additionalMaster.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                      Additional Master Data ({additionalMaster.length})
                    </h4>
                    <div className="space-y-1">
                      {additionalMaster.map((data, idx) => (
                        <div
                          key={`${data.entity}-${idx}`}
                          className="flex items-start justify-between gap-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs"
                        >
                          <div className="flex-1">
                            <span className="font-medium text-neutral-900 block">{data.entity}</span>
                            <span className="text-neutral-600 text-[10px]">
                              from {data.sourceSystem}
                            </span>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                            data.confidence === 'high'
                              ? 'bg-green-100 text-green-700'
                              : data.confidence === 'medium'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {data.confidence}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Transactional Data */}
                {additionalTransactional.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                      Additional Transactional Data ({additionalTransactional.length})
                    </h4>
                    <div className="space-y-1">
                      {additionalTransactional.map((data, idx) => (
                        <div
                          key={`${data.entity}-${idx}`}
                          className="flex items-start justify-between gap-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs"
                        >
                          <div className="flex-1">
                            <span className="font-medium text-neutral-900 block">{data.entity}</span>
                            <span className="text-neutral-600 text-[10px]">
                              from {data.sourceSystem}
                            </span>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                            data.confidence === 'high'
                              ? 'bg-green-100 text-green-700'
                              : data.confidence === 'medium'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {data.confidence}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-xs text-purple-800">
                    💡 <span className="font-semibold">Tip:</span> These additional data entities could enhance your graph solution. Consider whether they would help answer executive questions.
                  </p>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Action Items */}
      {unmappedRequirements > 0 && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50">
          <div className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-amber-900 mb-1">
                  Action Required: {unmappedRequirements} data {unmappedRequirements === 1 ? 'requirement' : 'requirements'} not mapped
                </h4>
                <p className="text-sm text-amber-800 mb-3">
                  Add more enterprise systems to cover these data requirements, or remove them if they're not actually needed.
                </p>
                <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                  {unmappedMaster.slice(0, 3).map(req => (
                    <li key={req.entity}>{req.entity} (Master Data - {req.priority} priority)</li>
                  ))}
                  {unmappedTransactional.slice(0, 3).map(req => (
                    <li key={req.entity}>{req.entity} (Transactional - {req.priority} priority)</li>
                  ))}
                  {(unmappedMaster.length + unmappedTransactional.length) > 6 && (
                    <li className="font-medium">...and {(unmappedMaster.length + unmappedTransactional.length) - 6} more</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
